import { defineNuxtPlugin, useRoute, useRuntimeConfig } from '#imports'

import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import {
  type EventEvent,
  type LogEvent,
  LogLevel,
  type MeasurementEvent,
  TransportItemType,
  getWebInstrumentations,
  initializeFaro,
} from '@grafana/faro-web-sdk'

import { getCacheStatus } from '../utils/getCacheStatus'
import { getConnection } from '../utils/getConnection'
import { getDeviceType } from '../utils/getDeviceType'
import { getRoutePath } from '../utils/getRoutePath'
import { getVerifiedBot } from '../utils/getVerifiedBot'
import { isCloudflareReportCspViolation } from '../utils/isCloudflareReportCspViolation'

export default defineNuxtPlugin(() => {
  if (process.client) {
    const publicConfig = useRuntimeConfig().public
    const config = publicConfig.instrumentation
    const route = useRoute()

    if (!config.grafanaFaroEnabled || !config.grafanaFrontendApplicationUrl)
      return

    // Captured once per page to avoid redundant DOM/window reads per metric.
    const webVitalsContext = {
      connection: getConnection(),
      deviceType: getDeviceType(),
      cachestatus: getCacheStatus(),
      verifiedbot: getVerifiedBot(),
    }
    const routePath = getRoutePath(route)
    const locale = useI18nLocale()

    initializeFaro({
      url: config.grafanaFrontendApplicationUrl,
      app: {
        name: publicConfig.SERVICE,
        version: publicConfig.VERSION,
        environment: publicConfig.ENVIRONMENT,
      },
      instrumentations: [
        ...getWebInstrumentations({
          // already propagated via front-logger library
          captureConsole: false,
        }),
      ],
      sessionTracking: { samplingRate: Number(config.samplingRate) },

      pageTracking: {
        page: {
          id: routePath,
          attributes: {
            locale,
          },
        },
      },

      view: {
        name: routePath,
      },

      ignoreErrors: [
        // Harmless browser layout quirk, not a real error
        /^ResizeObserver loop limit exceeded$/,
        /^ResizeObserver loop completed with undelivered notifications$/,
        // Cross-origin scripts with no useful stack
        /^Script error\.$/,
        // Browser extension interference
        /chrome-extension:\/\//,
        /moz-extension:\/\//,
        // iOS Edge translation — throws in bursts
        /^Promise does not exist for the provided text/,
        // Facebook/Instagram webview script injections
        /^FBNav/,
      ],

      beforeSend: (item) => {
        if (item.type === TransportItemType.LOG) {
          const log = item.payload as LogEvent

          // Facebook/Instagram webview injections (console.info calls)
          if (log.message.startsWith('FBNav')) return null

          // iOS Edge translation logs (error variant covered by ignoreErrors)
          if (
            log.message.startsWith(
              'Promise does not exist for the provided text',
            )
          )
            return null

          // AppsFlyer SDK verbose INFO logs (started 2025-09-30)
          if (log.level === LogLevel.INFO && log.message.startsWith('[AF-SDK]'))
            return null
        }

        // Enrich Core Web Vitals measurements with the perf-context fields we
        // previously sent to Noodle (network, device, cache, bot status).
        // Scoped to web-vitals so logs/errors aren't bloated.
        if (item.type === TransportItemType.MEASUREMENT) {
          const measurement = item.payload as MeasurementEvent
          if (measurement.type === 'web-vitals') {
            measurement.context = {
              ...measurement.context,
              ...webVitalsContext,
            }
          }
        }

        if (item.type === TransportItemType.EVENT) {
          const event = item.payload as EventEvent

          // Cloudflare injects its own CSP policies that report violations to
          // `cf-csp-endpoint`. Report-only violations are noisy and useless to us;
          // enforced ones are useful to see if Cloudflare starts blocking requests.
          if (isCloudflareReportCspViolation(event)) return null
        }

        return item
      },
    })
  }
})
